/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import {
    type BaseRequestOptionsArgs, type JobResponse,
    CORE_CONFIG, DomUtilService, EventService, HttpService, LocalStorageService, PermissionService, PromptService,
    SessionStorageService,
} from '@dm/core';
import { LicenseKey } from 'app/common/constants/license-key';
import { MO } from 'app/common/constants/mo';
import { type Host, type Initiator, type Lun, type Mapping } from 'app/common/types/mo';
import { SWITCH } from 'app/config/switch';
import { HostsService } from 'app/feature/service/block-service/hosts/hosts.service';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { type GuideConfig, type GuideInfo } from './smart-assistant';
import { type SmartAssistantEntryE, GuideCreateOptions, GuideEventKey, GuideStorageKey, PermissionCheckObjE } from './smart-assistant.const';
import { SmartAssistantStrService } from './smart-assistant-str.service';

@Injectable({
    providedIn: 'root',
})
export class SmartAssistantService {
    i18n = this.str.i18n;
    /**
     * 可以直接点击的元素
     */
    canClickClassList = ['btn-add', 'n-multi-select', 'collapse-switch'];
    /**
     * 不支持点击的元素
     */
    notSupportClickList = ['n-button-icon', 'n-button-content', 'n-card-panel-title-show-menus'];

    /**
     * 查询指定主机所关联的启动器
     */
    getHostInitiators = this.hostsService.getHostInitiators;

    constructor(
        private eventService: EventService,
        private sessionStorageService: SessionStorageService,
        private httpService: HttpService,
        private hostsService: HostsService,
        private domUtilService: DomUtilService,
        private promptService: PromptService,
        public str: SmartAssistantStrService,
        private localStorageService: LocalStorageService,
        private permissionService: PermissionService,
    ) { }

    /**
     * SessionStorage里获取智能助手配置信息
     */
    getGuideAllConfig(): GuideConfig {
        return this.sessionStorageService.get(GuideStorageKey.guideConfig) || {};
    }

    /**
     * SessionStorage里获取智能助手配置信息的某个参数
     */
    getGuideConfig(key: GuideStorageKey): any {
        const guideConfig = this.getGuideAllConfig();
        return guideConfig[key] ?? null;
    }

    /**
     * 从SessionStorage里获取操作类型和开始时间
     */
    getGuideInfo(): GuideInfo {
        const guideConfig = this.getGuideAllConfig();
        return {
            [GuideStorageKey.guideOperateType]: guideConfig.guideOperateType,
            [GuideStorageKey.startTime]: guideConfig.startTime,
        };
    }

    /**
     * 批量查询LUN并根据ID过滤
     */
    getLunBatch(idFilter: string, options?: BaseRequestOptionsArgs): Observable<Lun[]> {
        const rest = this.httpService.makeUrl('lun.getBatch').setFilters(
            [
                { field: 'ID', value: idFilter, matchMode: '::' },
            ],
        );
        return this.httpService.get(rest, options).pipe(map(({ data }: { data: Lun[] }) => data || []));
    }

    /**
     * 更新智能助手创建的LUN信息
     */
    lunPollingFn = (): void => {
        const lunList = this.getGuideConfig(GuideStorageKey.createdLuns);
        if (!lunList || !lunList.length) {
            return;
        }

        // 使用CFG提供的过滤方法，以下划线分隔ID
        const idFilter = lunList.map((lun: Lun) => lun.ID).join('_');

        // 单个查询每个LUN的信息
        this.getLunBatch(idFilter, { doException: false }).subscribe(
            (data: Lun[]) => {
                this.setLunList(data, true);
            },
            (res: JobResponse) => {
                this.setLunList(res.data ?? [], true);
            },
        );
    };

    /**
     * 通过接口查询主机信息
     */
    getHostByRest(id: string, options?: BaseRequestOptionsArgs): Observable<Host> {
        const rest = this.httpService.makeUrl('host.get').format(id);
        return this.httpService.get(rest, options).pipe(map(({ data }: { data: Host }) => data || {}));
    }

    /**
     * 更新智能助手创建的主机信息
     */
    hostPollingFn = (): void => {
        const host = this.getGuideConfig(GuideStorageKey.createdHost);
        if (!host || !Object.keys(host).length) {
            return;
        }

        this.getHostByRest(host.ID, { doException: false }).subscribe(
            (data: Host) => {
                this.setHostInfo(data, true);
            },
            (res: JobResponse) => {
                this.setHostInfo(res.data ?? {}, true);
            },
        );
    };

    /**
     * 查询主机映射的LUN
     * @param hostId 主机ID
     */
    getHostMapingLunByRest = (hostId: string, options?: BaseRequestOptionsArgs): Observable<Mapping[]> => {
        const rest = this.httpService.makeUrl('mapping.mappedObjects')
            .setParams({
                ASSOCIATEOBJTYPE: MO.HOST_TYPE,
                ASSOCIATEOBJID: hostId,
            });
        return this.httpService.get(rest, options).pipe(map(({ data }: { data: Mapping[] }) => data || []));
    };

    /**
     * 设置智能助手配置信息
     * @param key
     * @param value
     * @param emit 是否发送event更新
     */
    setGuideConfig(key: GuideStorageKey, value: any, emit = true): void {
        const guideConfig = this.getGuideAllConfig();
        guideConfig[key] = value;

        // 更新用户实际执行到的步骤
        const latestStepsValid = (!guideConfig.latestSteps || guideConfig.latestSteps < value);
        if (key === GuideStorageKey.step &&
            guideConfig.startGuiding &&
            latestStepsValid
        ) {
            guideConfig.latestSteps = value;
        }

        this.sessionStorageService.set(GuideStorageKey.guideConfig, guideConfig);

        if (emit) {
            this.emitGuideConfig();
        }
    }

    /**
     * 设置SessionStorage里的LUN
     */
    setLunList(lunList: Lun[], isPolling?: boolean): void {
        this.setGuideConfig(GuideStorageKey.createdLuns, lunList, false);
        this.eventService.emit(GuideEventKey.lunCreated, isPolling);
    }

    /**
     * 设置SessionStorage里的主机信息
     */
    setHostInfo(host: Host, isPolling?: boolean): void {
        this.setGuideConfig(GuideStorageKey.createdHost, host, false);
        this.eventService.emit(GuideEventKey.hostCreated, isPolling);
    }

    /**
     * 设置SessionStorage里的启动器
     */
    setInitiatorlist(initiatorList: Initiator[], isPolling?: boolean): void {
        this.setGuideConfig(GuideStorageKey.createdInitiator, initiatorList, false);
        this.eventService.emit(GuideEventKey.initiatorCreated, isPolling);
    }

    /**
     * 设置SessionStorage里的LUN和主机映射关系
     */
    setMaplist(mapList: Mapping[], isPolling?: boolean): void {
        this.setGuideConfig(GuideStorageKey.mappedLun, mapList, false);
        this.eventService.emit(GuideEventKey.mappedLun, isPolling);
    }

    /**
     * 将SessionStorage里“引导状态”和“创建更多”的状态设为false，“显示主按钮”设置为false
     */
    initStatus(): void {
        this.setGuideConfig(GuideStorageKey.startGuiding, false, false);
        this.setGuideConfig(GuideStorageKey.showPrimaryBtn, false, false);
        this.setGuideConfig(GuideStorageKey.stepForCreateMore, null);
    }

    /**
     * 结束未完成的进程并初始化配置参数
     */
    clearGuideProcess(): void {
        const config = this.getGuideAllConfig();
        const initConfig = {
            [GuideStorageKey.createdHost]: {},
            [GuideStorageKey.createdInitiator]: [],
            [GuideStorageKey.createdLuns]: [],
            [GuideStorageKey.guideOperateType]: null,
            [GuideStorageKey.stepForCreateMore]: null,
            [GuideStorageKey.mappedLun]: [],
            [GuideStorageKey.showSmartAssistant]: config.showSmartAssistant,
            [GuideStorageKey.startGuiding]: false,
            [GuideStorageKey.startTime]: null,
            [GuideStorageKey.step]: null,
            [GuideStorageKey.isHostDeleted]: false,
            [GuideStorageKey.isFinished]: false,
            [GuideStorageKey.latestSteps]: null,
            [GuideStorageKey.entry]: config.entry,
            [GuideStorageKey.showExportLog]: config.showExportLog,
            [GuideStorageKey.showPrimaryBtn]: false,
        };
        this.sessionStorageService.set(GuideStorageKey.guideConfig, initConfig);
        this.emitGuideConfig();
    }

    /**
     * 发送智能助手更新信息
     */
    emitGuideConfig(): void {
        const config = this.getGuideAllConfig();
        this.eventService.emit(GuideEventKey.assistantConfigChange, config);
    }

    /**
     * 检查步骤是否有值
     * @param step 要检查的步骤
     * @param checkFinished 增加步骤是否等于finished的判断
     */
    checkStepValid(step: GuideCreateOptions, checkFinished = false): boolean {
        if (checkFinished) {
            return (Object.values(GuideCreateOptions).includes(step)) && step !== GuideCreateOptions.Finished;
        }

        return Object.values(GuideCreateOptions).includes(step);
    }

    /**
     * 更新引导信息
     */
    updateGuideInfo = (evt: MouseEvent): void => {
        const guideConfig = this.getGuideAllConfig();
        if (!guideConfig.startGuiding || guideConfig.isFinished) {
            return;
        }

        const el: HTMLElement = evt.target as HTMLElement;
        const targetNodes = this.getNeedProcessNodes();
        for (const node of targetNodes) {
            if (node === el || this.domUtilService.isParentNode(el, node)) {
                this.setGuideConfig(GuideStorageKey.showPrimaryBtn, true, false);
                return;
            }
        }
    };

    /**
     * 返回某引导步骤需要特殊处理的元素
     */
    getNeedProcessNodes = (): Element[] => {
        const commonNodes = [];

        // 引导过程中，点击下列元素需要进行特殊处理
        const targetNodesMap: Map<GuideCreateOptions, Element[]> = new Map([
            [GuideCreateOptions.CreateLun, [
                // 左菜单-主机
                this.getTargetLeftMenuNode(this.i18n.hostLabel),
            ]],
            [GuideCreateOptions.CreateHost, [
                // 左菜单-LUN
                this.getTargetLeftMenuNode(this.i18n.lunTitle),
            ]],
            [GuideCreateOptions.AddInitiator, [
                // 左菜单-LUN
                this.getTargetLeftMenuNode(this.i18n.lunTitle),
            ]],
            [GuideCreateOptions.MapLunToHost, [
                // 左菜单-LUN
                this.getTargetLeftMenuNode(this.i18n.lunTitle),
            ]],
        ]);

        const moreStep = this.getGuideConfig(GuideStorageKey.stepForCreateMore);
        if (this.checkStepValid(moreStep, true)) {
            return [...targetNodesMap.get(moreStep), ...commonNodes];
        }

        const activeStep = this.getGuideConfig(GuideStorageKey.step);
        if (this.checkStepValid(activeStep, true)) {
            return [...targetNodesMap.get(activeStep), ...commonNodes];
        }

        return commonNodes;
    };

    /**
     * 获取左菜单的html元素
     * @param menuName 菜单名
     */
    getTargetLeftMenuNode = (menuName: string): any => {
        const liEls = document.querySelectorAll('n-panel-menu .n-panel-menu-list > .n-panel-menu-item .n-panel-menu-name');
        return Array.prototype.find.call(liEls, (el: HTMLElement) => el.textContent === menuName)?.parentNode || null;
    };

    /**
     * 触发停止引导的弹窗
     */
    getStopGuidingPrompt = (callback?: () => void): void => {
        this.promptService.info({
            id: 'prompt-smart-assistant-getStopGuidingPrompt',
            title: this.i18n.stopGuiding,
            content: this.i18n.stopGuidingTip,
            onOk: (): void => {
                this.promptService.close();
                this.setGuideConfig(GuideStorageKey.startGuiding, false);
                if (callback) {
                    callback();
                }
            },
        });
    };

    /**
     * 判断当前是否是某个引导步骤
     * @param step 要检查的步骤
     * @param isModifyHost 判断步骤是否是修改主机
     */
    isGuidingTargetStep(step: GuideCreateOptions, isModifyHost = false): boolean {
        const config = this.getGuideAllConfig();
        if (isModifyHost) {
            return config.step !== step && config.stepForCreateMore === step;
        }

        return config.step === step || config.stepForCreateMore === step;
    }

    /**
     * 检查每个步骤是否完成
     */
    async checkFinished(): Promise<boolean | void> {
        const guideConfig = this.getGuideAllConfig();
        if (!this.checkStepValid(guideConfig.step)) {
            return undefined as Promise<boolean | void>;
        }

        // 如果有对象被删除，也不算完成引导
        if (guideConfig.createdLuns.length === 0) {
            this.setGuideConfig(GuideStorageKey.step, GuideCreateOptions.CreateLun);
            return false;
        }

        if (Object.keys(guideConfig.createdHost).length === 0) {
            this.setGuideConfig(GuideStorageKey.step, GuideCreateOptions.CreateHost);
            return false;
        }

        if (guideConfig.createdInitiator.length === 0) {
            this.setGuideConfig(GuideStorageKey.step, GuideCreateOptions.AddInitiator);
            return false;
        }

        if (guideConfig.mappedLun.length === 0) {
            this.setGuideConfig(GuideStorageKey.step, GuideCreateOptions.MapLunToHost);
            return false;
        }

        // 当所有创建的LUN都被该主机映射，才算完成引导
        const mapDatas: Mapping[] = await this.getHostMapingLunByRest(
            guideConfig[GuideStorageKey.createdHost]?.ID,
            { doException: false },
        ).toPromise();
        const mapLunIds = [];
        mapDatas.forEach((mapData: Mapping) => {
            if (mapData.lunId) {
                mapLunIds.push(mapData.lunId);
            }
        });
        return guideConfig[GuideStorageKey.createdLuns].every((lun: Lun) => mapLunIds.includes(lun.ID));
    }

    /**
     * 标记用户已进入过极简模式
     * @param userId 用户标志
     */
    setEnteredSimpleModeTag(userId: string): void {
        const firstEnterList = this.localStorageService.get(GuideStorageKey.firstEnterSimpleMode) || [];
        firstEnterList.push(userId);
        this.localStorageService.set(GuideStorageKey.firstEnterSimpleMode, firstEnterList);
    }

    /**
     * 检查用户是否首次进入极简模式
     * @param userId 用户标志
     */
    isFirstEnterSimpleMode(userId: string): boolean {
        const firstEnterList = this.localStorageService.get(GuideStorageKey.firstEnterSimpleMode) || [];
        return !firstEnterList.includes(userId);
    }

    /**
     * 用户是否点击过“不再显示”极简/标准模式切换Tip
     */
    getUserHideTip(): boolean {
        const hideTipList = this.localStorageService.get(GuideStorageKey.hideSimpleModeTip) || {};
        return Boolean(hideTipList[CORE_CONFIG.userId]);
    }

    /**
     * 隐藏极简/标准模式切换Tip
     */
    setHideTip(): void {
        const showTipList = this.localStorageService.get(GuideStorageKey.hideSimpleModeTip) || {};
        showTipList[CORE_CONFIG.userId] = true;
        this.localStorageService.set(GuideStorageKey.hideSimpleModeTip, showTipList);
    }

    /**
     * 调用智能助手的入口变更
     * @param entry 调用位置
     * @param emit 是否发送变化
     */
    assistantEntryChange(entry: SmartAssistantEntryE, emit = true): void {
        this.setGuideConfig(GuideStorageKey.entry, entry, emit);
    }

    /**
     * 检查是否有相关权限
     * @param checkObj 要检查的对象
     */
    checkPermission(checkObj?: PermissionCheckObjE): boolean {
        // 日志导出
        const hasLogWrite = this.permissionService.checkShow(['alarm_W', 'running_data_W', 'performance_W']);
        if (checkObj === PermissionCheckObjE.Log) {
            return hasLogWrite;
        }

        // LUN、主机、启动器和映射
        const hasBlockServicePermission = this.permissionService.checkShow(
            ['lun_W', 'host_W', 'storage_pool_R', '@workload_type_R_lun_R', 'initiator_W', 'mapping_view_W'],
            [LicenseKey.SmartThin],
        );
        if (checkObj === PermissionCheckObjE.LunAndHost) {
            return hasBlockServicePermission;
        }

        // 文件系统
        const hasFsPermission = this.permissionService.checkShow(['file_system_W', 'task_W', '@workload_type_fs_R']) &&
            SWITCH.SUPPORT_NAS;
        if (checkObj === PermissionCheckObjE.Fs) {
            return hasFsPermission;
        }

        // 文件共享（要查LDAP、AD域等信息）
        const hasShareWrite = this.permissionService.checkShow(['share_W', 'domain_R', 'task_W']) && SWITCH.SUPPORT_NAS;
        if (checkObj === PermissionCheckObjE.FileShare) {
            return hasShareWrite;
        }

        // 数据保护
        const hasLunProtectWrite = this.permissionService.checkShow(['lun_W', 'lun_snapshot_W']);
        const hasFsProtectWrite = this.permissionService.checkShow(['file_system_W', 'fs_snapshot_W']);
        if (checkObj === PermissionCheckObjE.Protection) {
            return hasLunProtectWrite || hasFsProtectWrite;
        }

        // 告警和事件
        const hasAlarmRead = this.permissionService.hasPermission('alarm_R');
        if (checkObj === PermissionCheckObjE.AlarmAndEvent) {
            return hasAlarmRead;
        }

        // 报告
        const hasReportRead = this.permissionService.hasPermission('performance_R');
        if (checkObj === PermissionCheckObjE.Report) {
            return hasReportRead;
        }

        // 默认判断是否有智能助手所需的“写权限”
        return hasBlockServicePermission ||
            hasFsPermission ||
            hasShareWrite ||
            hasLunProtectWrite ||
            hasFsProtectWrite;
    }
}
